import { useRef, useState } from 'react';
import { LocateFixed, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import GlobeMap3D from '@/components/three/GlobeMap3D';
import { REGIONS } from './yieldConstants';

export function StepPlace({ form, setForm }) {
  const [focus, setFocus] = useState(form.lat && form.lng ? [Number(form.lat), Number(form.lng)] : null);
  const [detecting, setDetecting] = useState(false);
  const [showCoords, setShowCoords] = useState(false);
  const lastTyped = useRef('');

  const applyPoint = async (lat, lng) => {
    setForm(f => ({ ...f, lat: lat.toFixed(6), lng: lng.toFixed(6) }));
    setFocus([lat, lng]);
    setDetecting(true);
    try {
      const { data } = await api.post('/ai/region-from-point', { lat, lng });
      if (data.region) { setForm(f => ({ ...f, region: data.region, regionAuto: true })); }
      else toast.info('Regione non riconosciuta: sceglila dal menu');
    } catch { /* la regione resta modificabile a mano */ } finally { setDetecting(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocalizzazione non disponibile');
    navigator.geolocation.getCurrentPosition(
      (pos) => { applyPoint(pos.coords.latitude, pos.coords.longitude); toast.success('Posizione rilevata'); },
      () => toast.error('Impossibile rilevare la posizione: tocca il globo per sceglierla'),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const commitTyped = () => {
    const lat = Number(form.lat), lng = Number(form.lng);
    const key = `${form.lat}|${form.lng}`;
    if (form.lat && form.lng && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && key !== lastTyped.current) {
      lastTyped.current = key;
      applyPoint(lat, lng);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Tocca il globo sul tuo terreno (avvicinati con lo zoom) oppure usa la tua posizione: rileviamo regione, clima e altitudine in automatico.</p>
      <button type="button" onClick={useMyLocation} data-testid="yield-use-location"
              className="w-full h-12 rounded-full border border-chartreuse/40 bg-chartreuse/10 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-chartreuse/20 transition-colors">
        <LocateFixed size={16} className="text-chartreuse"/> Usa la mia posizione attuale
      </button>
      <div data-testid="yield-globe-picker">
        <GlobeMap3D height="300px" showHud={false} testId="yield-globe"
                    focus={focus || [42.2, 12.6]} zoom={focus ? 10 : 8.8}
                    pickedPoint={form.lat && form.lng ? { lat: Number(form.lat), lng: Number(form.lng) } : null}
                    onPick={(ll) => applyPoint(ll.lat, ll.lng)}/>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
        <Select value={form.region || '__none__'} onValueChange={v => setForm(f => ({ ...f, region: v === '__none__' ? '' : v, regionAuto: false }))}>
          <SelectTrigger data-testid="yield-region-select"><SelectValue placeholder="Regione"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Regione non indicata</SelectItem>
            {REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid="yield-region-status">
          {detecting ? <><Loader2 size={12} className="animate-spin"/> rilevo…</> : form.regionAuto ? <><MapPin size={12} className="text-chartreuse"/> rilevata</> : null}
        </span>
      </div>
      <button type="button" onClick={() => setShowCoords(s => !s)} className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" data-testid="yield-coords-toggle">
        {showCoords ? 'Nascondi coordinate' : 'Inserisci le coordinate a mano'}
      </button>
      {showCoords && (
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Latitudine" value={form.lat} onChange={e => setForm(f => ({ ...f, lat: e.target.value }))} data-testid="yield-lat-input"/>
          <Input placeholder="Longitudine" value={form.lng} onChange={e => setForm(f => ({ ...f, lng: e.target.value }))} onBlur={commitTyped} data-testid="yield-lng-input"/>
        </div>
      )}
    </div>
  );
}
